/**
 * 1.7.7 — the month's days: the weeks of the grid (Sunday first), the events of a month day by day, and the names
 * of the weekdays. Civil day keys only (`yyyyMMdd`); nothing here converts an instant.
 */
import { addDaysToKey, dayOfWeek, keyToNoonUtc } from '@shared/dates';
import { daysInMonth } from '@shared/event-calendar';
import type { EventView } from '@shared/events';
import { localeFor } from '@/lib/dates';

const pad = (n: number) => String(n).padStart(2, '0');

/** Every day of the month, first to last. */
export function daysOf(month: string): string[] {
  return Array.from({ length: daysInMonth(month) }, (_, i) => `${month}${pad(i + 1)}`);
}

/** The grid's rows: seven places a week, Sunday first; a place outside the month is null. */
export function weeksOf(month: string): (string | null)[][] {
  const cells: (string | null)[] = [...Array.from({ length: dayOfWeek(`${month}01`) }, () => null), ...daysOf(month)];
  while (cells.length % 7) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** The month's events by day, each day's in the order of the clock (an event with no time first), then by name. */
export function eventsByDay(events: readonly EventView[], month: string): Map<string, EventView[]> {
  const out = new Map<string, EventView[]>();
  const seen = new Set<string>();
  for (const e of events) {
    if (!e.dayKey.startsWith(month) || seen.has(e.eventId)) continue;
    seen.add(e.eventId);
    out.set(e.dayKey, [...(out.get(e.dayKey) ?? []), e]);
  }
  for (const list of out.values()) list.sort((a, b) => (a.minutes ?? -1) - (b.minutes ?? -1) || a.name.localeCompare(b.name));
  return new Map([...out.entries()].sort((a, b) => a[0].localeCompare(b[0])));
}

/** A Sunday, to name the weekdays from. */
const A_SUNDAY = '20230101';

/** The seven weekdays, Sunday first: "Sun" and "Sunday" in the person's language. */
export function weekdayNames(lang: string): { short: string; long: string }[] {
  const fmt = (weekday: 'short' | 'long') => new Intl.DateTimeFormat(localeFor(lang), { weekday, timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => {
    const at = new Date(keyToNoonUtc(addDaysToKey(A_SUNDAY, i)));
    return { short: fmt('short').format(at), long: fmt('long').format(at) };
  });
}

/** How many volunteers the event asks for (0 = none asked for) and how many said they are available. */
export function volunteersOf(e: EventView): { needed: number; going: number } {
  return { needed: e.numbers.volunteerPlaces ?? 0, going: e.three.places.volunteersGoing };
}
